import {
  ConstructionType,
  ConstructionYear,
  NumFamilies,
  SquareFootage,
  Zip,
} from "pages/underwriting/rating/ACVEstimator/ACVEstimator.types";
import { ACVEInput } from "pages/underwriting/rating/ACVEstimator/ACVEstimator.types";

const CONSTRUCTION_MULTIPLIERS: Record<NonNullable<ConstructionType>, number> = {
  F: 200,
  M: 203,
  //? Spreadsheet formula uses 203, but declares 215 for masonry
};

const FAMILY_MULTIPLIERS: Record<number, number> = {
  1: 0.9865,
  2: 1.1,
  3: 1.18,
  4: 1.25,
};

const BUFFALO_ZIP_PREFIXES = [140, 141, 142, 143];
const BUFFALO_ZIP_MULTIPLIER = 0.9; //? Previous raters have had .45, now .9

function isBuffaloZip(zipCode: string | number | undefined) {
  if (!zipCode) return false;
  const prefix = parseInt(zipCode.toString().substring(0, 3), 10);
  return BUFFALO_ZIP_PREFIXES.includes(prefix);
}

//* Calculated Values *//
export function calcEstimatedRCValue(
  squareFootage: SquareFootage,
  constructionType: ConstructionType,
  numFamilies: NumFamilies
): number {
  if (numFamilies <= 0 || squareFootage < 0 || squareFootage == null || isNaN(squareFootage) || !constructionType)
    return 0;
  const constructionMultiplier = CONSTRUCTION_MULTIPLIERS[constructionType];
  const familyMultiplier = FAMILY_MULTIPLIERS[numFamilies] ?? 1.25;

  return squareFootage * familyMultiplier * constructionMultiplier;
}

export function calcDepreciationPercentage(constructionYear: ConstructionYear): number {
  if (!constructionYear || constructionYear < 0) return 0;
  else if (constructionYear <= 1920) return 0.6;
  else if (constructionYear <= 1939) return 0.5;
  else if (constructionYear <= 1959) return 0.4;
  else if (constructionYear <= 1970) return 0.3; //? Forumla says 1970, but it's probably 1979
  else if (constructionYear <= 1999) return 0.2;
  else return 0.1;
}

//* Results *//
export function calcEstimatedActualCashValue(
  estimatedRCValue: number,
  depreciationPercentage: number,
  zipCode: Zip
): number {
  let estimatedACV = estimatedRCValue * (1 - depreciationPercentage);
  if (isBuffaloZip(zipCode)) {
    estimatedACV *= BUFFALO_ZIP_MULTIPLIER;
  }

  return estimatedACV;
}

export function calcDebrisRemovalEstimate(estimatedActualCashValue: number): number {
  return estimatedActualCashValue > 0 ? estimatedActualCashValue * 0.05 : 0;
}

export function calcTotalCoverageALimit(estimatedActualCashValue: number, debrisRemovalEstimate: number): number {
  return estimatedActualCashValue + debrisRemovalEstimate;
}

export function reportEstimate(input: ACVEInput) {
  const rcv = calcEstimatedRCValue(input.squareFootage, input.constructionType, input.numFamilies);
  const dep = calcDepreciationPercentage(input.constructionYear);
  const acv = calcEstimatedActualCashValue(rcv, dep, input.zip);
  const debris = calcDebrisRemovalEstimate(acv);
  const total = calcTotalCoverageALimit(acv, debris);

  return {
    estimatedRCValue: rcv,
    acv: Math.round(acv),
    isBelowMinimum: acv < 30000,
    debrisRemovalEstimate: Math.round(debris),
    totalCoverageALimit: Math.round(total),
    maxCoverage: Math.round(total * 1.5),
  };
}
